import React, { useState } from 'react';
import { Users, ArrowLeft, Loader, Star, Sparkles, UserCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Reusable primitives ───────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
  <h3 className="text-lg font-bold text-gray-900 mt-8 mb-4 pb-2 border-b border-sky-100">{children}</h3>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between py-2.5 cursor-pointer group">
    <span className="text-sm text-gray-700 group-hover:text-[#094f6f]">{label}</span>
    <div onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${checked ? 'bg-[#094f6f]' : 'bg-gray-200'}`}>
      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </div>
  </label>
);

const ChipGroup = ({ label, options, value, onChange, multi = false }) => {
  const isSelected = (opt) => multi ? (value || []).includes(opt) : value === opt;
  const handleClick = (opt) => {
    if (multi) {
      const cur = value || [];
      onChange(cur.includes(opt) ? cur.filter(v => v !== opt) : [...cur, opt]);
    } else onChange(value === opt ? null : opt);
  };
  return (
    <div className="mb-3">
      {label && <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</p>}
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button key={opt} type="button" onClick={() => handleClick(opt)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
              isSelected(opt) ? 'bg-[#094f6f] text-white border-[#094f6f] shadow-sm' : 'border-gray-200 text-gray-600 bg-white hover:border-[#094f6f] hover:text-[#094f6f]'
            }`}>{opt}</button>
        ))}
      </div>
    </div>
  );
};

const Slider = ({ label, value, min, max, onChange }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-2">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <span className="text-sm font-bold text-[#094f6f]">{value}</span>
    </div>
    <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))}
      className="w-full accent-[#094f6f] h-2 rounded-full cursor-pointer" />
    <div className="flex justify-between text-xs text-gray-400 mt-1"><span>{min}</span><span>{max}</span></div>
  </div>
);

const RangeSlider = ({ label, minVal, maxVal, min, max, onChangeMin, onChangeMax }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-2">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <span className="text-sm font-bold text-[#094f6f]">A${minVal} – A${maxVal}</span>
    </div>
    <div className="space-y-1">
      <input type="range" min={min} max={max} value={minVal} onChange={e => { const v = Number(e.target.value); if (v < maxVal) onChangeMin(v); }} className="w-full accent-[#094f6f] h-2 rounded-full cursor-pointer" />
      <input type="range" min={min} max={max} value={maxVal} onChange={e => { const v = Number(e.target.value); if (v > minVal) onChangeMax(v); }} className="w-full accent-[#094f6f] h-2 rounded-full cursor-pointer" />
    </div>
    <div className="flex justify-between text-xs text-gray-400 mt-1"><span>A${min}</span><span>A${max}</span></div>
  </div>
);

const INIT = {
  isClean: false, isSocial: false, isQuiet: false,
  smoking: null, drinking: null, parties: null, schedule: null,
  guestFrequency: 2, diet: null, studyStyle: null, universityCourse: '',
  employment: null, hobbies: '', communication: null, sharedActivities: '',
  personality: null, petsOk: null, budgetMin: 300, budgetMax: 1200,
  utilities: null, bills: null, genderPref: null, values: [], workStyle: null,
};

// ── Match card ─────────────────────────────────────────────────────────────────
const MatchCard = ({ match }) => {
  const scoreColor = match.score >= 85 ? 'bg-green-500' : match.score >= 72 ? 'bg-[#094f6f]' : 'bg-yellow-500';
  return (
    <div className="bg-white rounded-2xl border border-sky-100 shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
          {match.avatar_letter || match.name?.[0] || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <p className="font-bold text-gray-900">{match.name}</p>
            <div className={`flex items-center gap-1 ${scoreColor} text-white text-xs px-2 py-0.5 rounded-full font-bold`}>
              <Star className="w-3 h-3 fill-white" />
              {match.score}%
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-1">{match.university}</p>
          {match.course && <p className="text-xs text-[#094f6f] mb-2">{match.course}</p>}
          {match.bio && <p className="text-xs text-gray-600 mb-2 leading-relaxed">{match.bio}</p>}
          <div className="flex flex-wrap gap-1">
            {(match.tags || []).map(t => (
              <span key={t} className="text-[10px] bg-sky-50 text-[#094f6f] px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
          {match.source === 'ai' && (
            <div className="flex items-center gap-1 mt-2">
              <Sparkles className="w-3 h-3 text-purple-400" />
              <span className="text-[10px] text-purple-400">AI-suggested profile</span>
            </div>
          )}
          {match.source === 'real' && (
            <div className="flex items-center gap-1 mt-2">
              <UserCheck className="w-3 h-3 text-green-500" />
              <span className="text-[10px] text-green-600">Real RentalX member</span>
            </div>
          )}
        </div>
      </div>
      <button className="mt-4 w-full border border-[#094f6f] text-[#094f6f] py-2 rounded-xl text-sm font-semibold hover:bg-sky-50 transition-colors">
        Connect with {match.name?.split(' ')[0]}
      </button>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function VibeMate() {
  const { token } = useAuth();
  const [form, setForm] = useState(INIT);
  const [step, setStep] = useState('form');
  const [matches, setMatches] = useState([]);
  const [realCount, setRealCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Save profile
      await fetch(`${API}/api/vibe/profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      // Get matches
      const res = await fetch(`${API}/api/vibe/matches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMatches(data.matches || []);
      setRealCount(data.real_count || 0);
      setStep('results');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'results') return (
    <div className="max-w-2xl">
      <button onClick={() => setStep('form')} className="flex items-center gap-2 text-[#094f6f] text-sm font-medium mb-6 hover:opacity-70">
        <ArrowLeft className="w-4 h-4" /> Edit My Profile
      </button>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
          <Users className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Your VibeMates</h2>
          <p className="text-sm text-gray-500">
            {realCount > 0 ? `${realCount} real member${realCount > 1 ? 's' : ''} + ` : ''}{matches.length - realCount} AI-suggested profiles · sorted by compatibility
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {matches.map((m, i) => <MatchCard key={m.id || i} match={m} />)}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
          <Users className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">VibeMate™</h2>
          <p className="text-sm text-gray-500">Find your perfect housemate based on lifestyle compatibility</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-sky-100 shadow-sm p-6">
        <SectionHeading>Lifestyle & Habits</SectionHeading>
        <div className="divide-y divide-gray-50 mb-4">
          <Toggle label="I am a clean person" checked={form.isClean} onChange={v => set('isClean', v)} />
          <Toggle label="I prefer a social environment" checked={form.isSocial} onChange={v => set('isSocial', v)} />
          <Toggle label="I like a quiet environment" checked={form.isQuiet} onChange={v => set('isQuiet', v)} />
        </div>
        <ChipGroup options={['Non-smoker', 'Smoker']} value={form.smoking} onChange={v => set('smoking', v)} />
        <ChipGroup options={['Non-drinker', 'Drinker']} value={form.drinking} onChange={v => set('drinking', v)} />
        <ChipGroup options={['No parties', 'Occasional parties', 'Frequent parties']} value={form.parties} onChange={v => set('parties', v)} />
        <ChipGroup options={['Early bird', 'Night owl', 'Flexible']} value={form.schedule} onChange={v => set('schedule', v)} />
        <Slider label="Guest frequency (1–5)" value={form.guestFrequency} min={1} max={5} onChange={v => set('guestFrequency', v)} />
        <ChipGroup options={['Vegetarian', 'Vegan', 'Non-vegetarian', 'No preference']} value={form.diet} onChange={v => set('diet', v)} />

        <SectionHeading>Academic & Professional Life</SectionHeading>
        <ChipGroup label="Study style" options={['Structured', 'Flexible', 'Collaborative']} value={form.studyStyle} onChange={v => set('studyStyle', v)} />
        <div className="mb-3">
          <input type="text" value={form.universityCourse} onChange={e => set('universityCourse', e.target.value)}
            placeholder="University / Course (e.g. UOW — Engineering)"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
        </div>
        <ChipGroup options={['Employed', 'Student', 'Unemployed']} value={form.employment} onChange={v => set('employment', v)} />

        <SectionHeading>Personal Interests & Personality</SectionHeading>
        <textarea value={form.hobbies} onChange={e => set('hobbies', e.target.value)} placeholder="Hobbies..." rows={2}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
        <ChipGroup label="Communication style" options={['Direct', 'Indirect', 'Open']} value={form.communication} onChange={v => set('communication', v)} />
        <ChipGroup label="Personality" options={['Introverted', 'Extroverted', 'Ambivert']} value={form.personality} onChange={v => set('personality', v)} />

        <SectionHeading>Practical & Shared Living</SectionHeading>
        <ChipGroup label="Comfortable with pets?" options={['Yes', 'No', 'Negotiable']} value={form.petsOk} onChange={v => set('petsOk', v)} />
        <RangeSlider label="Weekly budget range (AUD)" minVal={form.budgetMin} maxVal={form.budgetMax} min={100} max={2000}
          onChangeMin={v => set('budgetMin', v)} onChangeMax={v => set('budgetMax', v)} />
        <ChipGroup label="Utilities" options={['Shared', 'Individual', 'Flexible']} value={form.utilities} onChange={v => set('utilities', v)} />
        <ChipGroup label="Preferred housemate gender" options={['Male', 'Female', 'Any', 'No preference']} value={form.genderPref} onChange={v => set('genderPref', v)} />

        <SectionHeading>Values & Priorities</SectionHeading>
        <ChipGroup multi options={['Values personal space highly', 'Comfortable with communal living', 'Always pays on time', 'Prefers direct discussion', 'Seeks compromise']}
          value={form.values} onChange={v => set('values', v)} />
        <ChipGroup label="Work/collaboration style" options={['Collaborative', 'Independent', 'Compromising']} value={form.workStyle} onChange={v => set('workStyle', v)} />

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        <button type="button" onClick={handleSubmit} disabled={loading}
          className="w-full mt-6 text-white py-4 rounded-2xl font-bold text-base hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}
          data-testid="vibe-submit">
          {loading ? <><Loader className="w-5 h-5 animate-spin" /> Finding your VibeMates...</> : <><Users className="w-5 h-5" /> Find My VibeMates</>}
        </button>
      </div>
    </div>
  );
}
